import { TextField, VFFTextFieldAdapter } from '@eventbrite/design-ops-ds';
import { Icon } from '@eventbrite/eds-icon';
import { CreditCardFront } from '@eventbrite/eds-iconography';
import { InputField } from '@eventbrite/eds-input-field';
import { PaymentIcon } from '@eventbrite/eds-payment-icon';
import { AVAILABLE_CARD_TYPES, getCardDef } from '@eventbrite/eds-utils';
import { ValidationFormField } from '@eventbrite/eds-validation-form-field';
import { gettext } from '@eventbrite/i18n';
import React, { FunctionComponent } from 'react';
import { checkCardImprovementsFeatureGate } from '../../utils/statsig';
import { CREDIT_CARD_FIELD_NAME, MAX_CREDIT_CARD_LENGTH } from './constants';
import './paymentInfoFields.scss';
import type { ShouldDisplayError } from './types';

export const CreditCardField: FunctionComponent<{
    cardType: string | null;
    onChange: (value: string, name: string) => void;
    onBlur: (value: string, name: string) => void;
    value?: string;
    shouldDisplayError?: ShouldDisplayError;
    shouldDisplayRequired?: boolean;
    paymentConstraintAnnotationNote: React.ReactNode;
    constrainedPaymentMethodError?: boolean;
}> = ({
    cardType,
    onChange,
    onBlur,
    value,
    shouldDisplayError,
    shouldDisplayRequired,
    paymentConstraintAnnotationNote,
    constrainedPaymentMethodError,
}) => {
        const getCardIconAndMaxLength = (cardType: string | null) => {
            const cardTypeInfo = cardType ? getCardDef(cardType) : '';
            return AVAILABLE_CARD_TYPES.includes(cardType)
                ? {
                    creditCardIcon: (
                        <PaymentIcon type={cardTypeInfo.icon} size="small" />
                    ),
                    creditCardMaxLength: cardTypeInfo.maxLength,
                }
                : {
                    creditCardIcon: (
                        <Icon
                            type={<CreditCardFront />}
                            size="small"
                            color="grey-300"
                        />
                    ),
                    creditCardMaxLength: MAX_CREDIT_CARD_LENGTH,
                };
        };

        const { creditCardIcon, creditCardMaxLength } =
            getCardIconAndMaxLength(cardType);
        const validationFormShouldDisplayError = constrainedPaymentMethodError
            ? () => true
            : shouldDisplayError;

        const cardNumberInput = checkCardImprovementsFeatureGate() ? (
            <VFFTextFieldAdapter onBlur={onBlur} onChange={onChange}>
                <TextField
                    autoComplete="cc-number"
                    data-automation="credit-card-field"
                    helperText={paymentConstraintAnnotationNote}
                    id="credit-card-number"
                    label={gettext('Card number')}
                    maxLength={creditCardMaxLength}
                    placeholder={gettext('e.g. 1111222233334444')}
                    required={shouldDisplayRequired}
                    suffix={creditCardIcon}
                    type="number"
                    value={value}
                />
            </VFFTextFieldAdapter>
        ) : (
            <InputField
                annotationNote={paymentConstraintAnnotationNote}
                autoComplete="cc-number"
                id="credit-card-number"
                label={gettext('Card number')}
                required={shouldDisplayRequired}
                maxLength={creditCardMaxLength}
                onBlur={onBlur}
                onChange={onChange}
                placeholder={gettext('e.g. 1111222233334444')}
                suffix={creditCardIcon}
                type="number"
                value={value}
                data-automation="credit-card-field"
            />
        );

        return (
            <ValidationFormField
                shouldDisplayError={validationFormShouldDisplayError}
                name={CREDIT_CARD_FIELD_NAME}
                required={shouldDisplayRequired}
                v2={true}
            >
                {cardNumberInput}
            </ValidationFormField>
        );
    };
